<script>
import axios from 'axios';
import LoadingState from "@/components/InvestigationEngine/States/Loading.vue";
import ErrorState from "@/components/InvestigationEngine/States/Error.vue";

const REFINEMENT_CATEGORIES = [
    { value: 'device_offline', label: 'Device offline / not reporting' },
    { value: 'hub_connectivity', label: 'Hub connectivity' },
    { value: 'power_battery', label: 'Power / battery' },
    { value: 'probe_issues', label: 'Probe issues' },
    { value: 'probe_calibration', label: 'Probe calibration' },
    { value: 'alarm_configuration', label: 'Alarm profile / configuration' },
    { value: 'diagnostic_readings', label: 'Diagnostic / sensor readings' },
];

export default {
    components: {
        LoadingState,
        ErrorState,
    },

    props: {
        serial: {
            type: String,
            required: true,
        },
        deviceType: {
            type: String,
            default: '',
        },
        results: {
            type: Array,
            default: () => [],
        },
        checksReady: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            loading: false,
            analysisRequested: false,
            analysis: null,
            error: null,
            isCollapsed: false,
            messages: [],
            refinement: {
                selectedCategories: [],
                text: '',
                submitting: false,
                error: null,
            },
            feedback: {
                rating: null,
                reasons: [],
                comment: '',
                submitted: false,
                submitting: false,
            },
            showFeedbackModal: false,
        };
    },

    computed: {
        refinementCategories() {
            return REFINEMENT_CATEGORIES;
        },

        canSubmitRefinement() {
            return (
                this.refinement.selectedCategories.length > 0
                || this.refinement.text.trim().length > 0
            );
        },

        feedbackReasons() {
            if (this.feedback.rating === 'yes') {
                return [
                    'Identified a problem',
                    'Prioritized what to fix',
                    'Understood the situation faster',
                    'Took action or made a decision',
                    'Increased confidence',
                    'Gave me new information',
                ];
            } else if (this.feedback.rating === 'unsure') {
                return [
                    'Not enough evidence or detail',
                    'Did not trust the conclusion',
                    'Hard to understand',
                    'Not relevant to my situation',
                    'Might be useful later',
                    'Needed more context',
                ];
            } else if (this.feedback.rating === 'no') {
                return [
                    'Incorrect or misleading',
                    'Based on bad data',
                    'Too late to be useful',
                    'Too noisy or obvious',
                    'I already knew this',
                    'Not relevant',
                ];
            }
            return [];
        },

        feedbackModalTitle() {
            if (this.feedback.rating === 'yes') {
                return 'What did this help you do?';
            } else if (this.feedback.rating === 'unsure') {
                return 'What made you unsure?';
            } else if (this.feedback.rating === 'no') {
                return 'What went wrong?';
            }
            return '';
        },

        feedbackModalSubtitle() {
            if (this.feedback.rating === 'yes') {
                return 'Select any that apply';
            } else if (this.feedback.rating === 'unsure') {
                return 'Select any that apply';
            } else if (this.feedback.rating === 'no') {
                return 'How can we improve?';
            }
            return '';
        },
    },

    watch: {
        checksReady: {
            immediate: true,
            handler(ready) {
                if (ready && !this.analysisRequested) {
                    this.fetchAIAnalysis();
                }
            },
        },
    },

    methods: {
        async fetchAIAnalysis() {
            if (this.analysisRequested) {
                return;
            }
            this.analysisRequested = true;

            try {
                this.loading = true;
                this.error = null;

                const response = await axios.post(
                    `/api-internal/investigation-engine/${this.serial}/ai-analysis`,
                    {
                        results: this.results,
                        device_type: this.deviceType,
                    }
                );

                if (response.data.ai_analysis?.success) {
                    this.analysis = response.data.ai_analysis;
                    this.messages = response.data.messages || [];
                } else {
                    this.error = response.data.ai_analysis?.error || 'Analysis failed';
                }
            } catch (error) {
                console.error('Failed to fetch AI analysis:', error);
                this.error = error.response?.data?.error || 'Failed to load analysis';
            } finally {
                this.loading = false;
            }
        },

        toggleRefinementCategory(value) {
            const idx = this.refinement.selectedCategories.indexOf(value);
            if (idx > -1) {
                this.refinement.selectedCategories.splice(idx, 1);
            } else {
                this.refinement.selectedCategories.push(value);
            }
        },

        async submitRefinement() {
            if (!this.canSubmitRefinement) {
                return;
            }

            try {
                this.refinement.submitting = true;
                this.refinement.error = null;

                const response = await axios.post(
                    `/api-internal/investigation-engine/${this.serial}/ai-analysis/refine`,
                    {
                        messages: this.messages,
                        categories: this.refinement.selectedCategories,
                        text: this.refinement.text,
                    }
                );

                if (response.data.ai_analysis?.success) {
                    this.analysis = response.data.ai_analysis;
                    this.messages = response.data.messages || this.messages;
                    this.refinement.selectedCategories = [];
                    this.refinement.text = '';
                } else {
                    this.refinement.error = response.data.ai_analysis?.error
                        || response.data.error
                        || 'Refinement failed';
                }
            } catch (error) {
                console.error('Failed to refine analysis:', error);
                this.refinement.error = error.response?.data?.error || 'Refinement request failed';
            } finally {
                this.refinement.submitting = false;
            }
        },

        toggleCollapsed() {
            this.isCollapsed = !this.isCollapsed;
        },

        handleFeedbackRating(rating) {
            this.feedback.rating = rating;
            this.feedback.reasons = [];
            this.showFeedbackModal = true;
        },

        toggleReason(reason) {
            const index = this.feedback.reasons.indexOf(reason);
            if (index > -1) {
                this.feedback.reasons.splice(index, 1);
            } else {
                this.feedback.reasons.push(reason);
            }
        },

        async submitFeedback() {
            try {
                this.feedback.submitting = true;

                const feedbackData = {
                    rating: this.feedback.rating,
                    reasons: this.feedback.reasons,
                    comment: this.feedback.comment,
                    analysis_output: {
                        overall_status: this.analysis.overall_status,
                        summary: this.analysis.summary,
                        critical_issues: this.analysis.critical_issues,
                        warnings: this.analysis.warnings,
                        recommendations: this.analysis.recommendations,
                        model: this.analysis.model,
                        usage: this.analysis.usage,
                    },
                    serial: this.serial,
                    timestamp: new Date().toISOString(),
                };

                await axios.post(`/api-internal/investigation-engine/${this.serial}/ai-analysis/feedback`, feedbackData);

                this.feedback.submitted = true;
                this.showFeedbackModal = false;
            } catch (error) {
                console.error('Failed to submit feedback:', error);
                alert('Failed to submit feedback. Please try again.');
            } finally {
                this.feedback.submitting = false;
            }
        },

        cancelFeedback() {
            this.showFeedbackModal = false;
            this.feedback.rating = null;
            this.feedback.reasons = [];
            this.feedback.comment = '';
        },
    },
};
</script>

<template>
    <div>
        <div
            @click="toggleCollapsed"
            :class="['flex justify-between items-start cursor-pointer hover:bg-gray-50 p-2 -m-2 rounded transition-colors duration-200', isCollapsed ? '-mb-2' : 'mb-6']"
        >
            <div class="flex items-center">
                <svg
                    :class="['w-4 h-4 mr-3 transition-transform duration-200 text-gray-400', isCollapsed ? '' : 'rotate-90']"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>

                <div>
                    <h3 class="text-left text-gray-900 font-semibold m-0 p-0 text-xl tracking-tight">
                        Analysis
                    </h3>
                </div>

                <span class="text-gray-700 ml-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M8 16v-6a2 2 0 1 1 4 0v6" /><path d="M8 13h4" /><path d="M16 8v8" /></svg>
                </span>

            </div>
        </div>

        <div v-if="!isCollapsed">
            <LoadingState
                v-if="!checksReady"
                message="Waiting for all checks to finish..."
                subtitle="Analysis will start once every check has reported in"
            />

            <LoadingState
                v-else-if="loading"
                message="Analyzing investigation results..."
                subtitle="This may take a few seconds"
            />

            <ErrorState
                v-else-if="error"
                :error="error"
            />

            <div v-else-if="analysis" class="flex gap-6">
                <div class="flex-1 space-y-6">

                    <div v-if="analysis.summary">
                        <p class="text-xs font-medium text-gray-700 uppercase tracking-wide p-0 m-0 mb-2">Summary</p>
                        <p class="text-base text-gray-900 leading-relaxed m-0 p-0">{{ analysis.summary }}</p>
                    </div>

                    <div v-if="analysis.critical_issues && analysis.critical_issues.length > 0">
                        <p class="text-xs font-medium text-gray-700 uppercase tracking-wide p-0 m-0 mb-2">Critical Issues</p>
                        <ul class="space-y-2">
                            <li v-for="(issue, idx) in analysis.critical_issues" :key="idx"
                                class="flex items-start gap-2 text-base text-gray-900">
                                <span class="text-red-500 mt-0.5">•</span>
                                <span>{{ issue }}</span>
                            </li>
                        </ul>
                    </div>

                    <div v-if="analysis.warnings && analysis.warnings.length > 0">
                        <p class="text-xs font-medium text-gray-700 uppercase tracking-wide p-0 m-0 mb-2">Warnings</p>
                        <ul class="space-y-2">
                            <li v-for="(warning, idx) in analysis.warnings" :key="idx"
                                class="flex items-start gap-2 text-base text-gray-900">
                                <span class="text-yellow-500 mt-0.5">•</span>
                                <span>{{ warning }}</span>
                            </li>
                        </ul>
                    </div>

                    <div v-if="analysis.recommendations && analysis.recommendations.length > 0">
                        <p class="text-xs font-medium text-gray-700 uppercase tracking-wide p-0 m-0 mb-2">Recommendations</p>
                        <ol class="space-y-2">
                            <li v-for="(rec, idx) in analysis.recommendations" :key="idx"
                                class="flex items-start gap-3 text-base text-gray-900">
                                <span class="text-gray-500 font-medium">{{ idx + 1 }}.</span>
                                <span class="flex-1">{{ rec }}</span>
                            </li>
                        </ol>
                    </div>

                    <div class="pt-6 border-t border-gray-200">
                        <p class="text-xs font-medium text-gray-700 uppercase tracking-wide mb-3">
                            Refine this analysis
                        </p>

                        <p class="text-sm text-gray-500 mb-4 m-0 p-0">
                            Select focus areas and / or add context to steer the analysis.
                        </p>

                        <div class="flex flex-wrap gap-2 mb-4">
                            <button
                                v-for="category in refinementCategories"
                                :key="category.value"
                                type="button"
                                @click="toggleRefinementCategory(category.value)"
                                :disabled="refinement.submitting"
                                :class="[
                                    'px-3 py-2 text-sm rounded-lg border-2 transition-all disabled:opacity-50',
                                    refinement.selectedCategories.includes(category.value)
                                        ? 'bg-blue-50 border-blue-600 text-blue-700 font-medium'
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-blue-700/20 hover:bg-blue-50'
                                ]"
                            >
                                {{ category.label }}
                            </button>
                        </div>

                        <textarea
                            v-model="refinement.text"
                            :disabled="refinement.submitting"
                            rows="3"
                            maxlength="2000"
                            placeholder="Add context about what you are seeing, what you have already tried, or what you want the AI to focus on..."
                            class="w-full border-2 border-gray-200 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors mb-3 disabled:opacity-50"
                        ></textarea>

                        <div class="flex items-center gap-3">
                            <button
                                type="button"
                                @click="submitRefinement"
                                :disabled="refinement.submitting || !canSubmitRefinement"
                                class="px-6 py-2 text-sm font-medium text-white rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {{ refinement.submitting ? 'Refining...' : 'Refine analysis' }}
                            </button>

                            <p v-if="refinement.error" class="text-sm text-red-600 m-0 p-0">
                                {{ refinement.error }}
                            </p>
                        </div>
                    </div>

                    <div v-if="analysis.usage" class="pt-4 border-t border-gray-200">
                        <details class="text-xs text-gray-500">
                            <summary class="cursor-pointer hover:text-gray-700">Details</summary>
                            <div class="mt-2 space-y-1">
                                <p>Model: {{ analysis.model }}</p>
                                <p>Input Tokens: {{ analysis.usage.input_tokens?.toLocaleString() }}</p>
                                <p>Output Tokens: {{ analysis.usage.output_tokens?.toLocaleString() }}</p>
                            </div>
                        </details>
                    </div>
                </div>

                <div class="w-72 flex-shrink-0">
                    <div v-if="!feedback.submitted" class="sticky top-4">
                        <div class="bg-white border-2 border-blue-100 rounded-md p-5 transition-colors hover:border-blue-200">
                            <div class="absolute top-0 left-0 right-0 h-1 bg-blue-700 rounded-t-md"></div>

                            <div class="mb-4 pt-1">
                                <div class="flex items-center gap-2 mb-1">
                                    <span class="text-blue-700">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 11v.01" /><path d="M8 11v.01" /><path d="M16 11v.01" /><path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3l12 0" /></svg>
                                    </span>
                                    <h4 class="text-sm font-semibold text-gray-900 m-0 p-0">Was this insight helpful?</h4>
                                </div>
                            </div>

                            <div class="space-y-2">
                                <button
                                    @click.stop="handleFeedbackRating('yes')"
                                    class="w-full px-3 py-2.5 text-sm font-medium rounded-lg border-2 transition-all bg-white border-gray-200 text-gray-700 hover:border-green-700/20 hover:text-green-700 hover:bg-green-50"
                                >
                                    <div class="flex items-center justify-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 11v8a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-7a1 1 0 0 1 1 -1h3a4 4 0 0 0 4 -4v-1a2 2 0 0 1 4 0v5h3a2 2 0 0 1 2 2l-1 5a2 3 0 0 1 -2 2h-7a3 3 0 0 1 -3 -3" /></svg>
                                        <span>Yes</span>
                                    </div>
                                </button>

                                <button
                                    @click.stop="handleFeedbackRating('unsure')"
                                    class="w-full px-3 py-2.5 text-sm font-medium rounded-lg border-2 transition-all bg-white border-gray-200 text-gray-700 hover:border-yellow-700/20 hover:text-yellow-700 hover:bg-yellow-50"
                                >
                                    <div class="flex items-center justify-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M8 8a3.5 3 0 0 1 3.5 -3h1a3.5 3 0 0 1 3.5 3a3 3 0 0 1 -2 3a3 4 0 0 0 -2 4" /><path d="M12 19l0 .01" /></svg>                                        <span>Unsure</span>
                                    </div>
                                </button>

                                <button
                                    @click.stop="handleFeedbackRating('no')"
                                    class="w-full px-3 py-2.5 text-sm font-medium rounded-lg border-2 transition-all bg-white border-gray-200 text-gray-700 hover:border-red-700/20 hover:text-red-700 hover:bg-red-50"
                                >
                                    <div class="flex items-center justify-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 13v-8a1 1 0 0 0 -1 -1h-2a1 1 0 0 0 -1 1v7a1 1 0 0 0 1 1h3a4 4 0 0 1 4 4v1a2 2 0 0 0 4 0v-5h3a2 2 0 0 0 2 -2l-1 -5a2 3 0 0 0 -2 -2h-7a3 3 0 0 0 -3 3" /></svg>
                                        <span>No</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-else class="sticky top-4">
                        <div class="bg-white border-2 border-emerald-200 rounded-md p-5">
                            <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-t-md"></div>

                            <div class="flex items-start gap-2 pt-1">
                                <svg class="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                <div>
                                    <h4 class="text-sm font-semibold text-gray-900 m-0 p-0">Thank you!</h4>
                                    <p class="text-sm text-gray-600 mt-1 m-0 p-0">Your feedback has been received.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="showFeedbackModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click.self="cancelFeedback">
            <div class="bg-white rounded-lg max-w-lg w-full mx-4">
                <div class="p-6">
                    <div class="mb-5">
                        <h3 class="text-lg font-semibold text-gray-900 m-0 p-0 mb-2">
                            {{ feedbackModalTitle }}
                        </h3>
                        <p class="text-base text-gray-600 m-0 p-0">
                            {{ feedbackModalSubtitle }}
                        </p>
                    </div>

                    <div class="mb-5">
                        <div class="flex flex-wrap gap-2">
                            <button
                                v-for="reason in feedbackReasons"
                                :key="reason"
                                @click="toggleReason(reason)"
                                :class="[
                                    'px-3 py-2 text-sm rounded-lg border-2 transition-all',
                                    feedback.reasons.includes(reason)
                                        ? 'bg-blue-50 border-blue-600 text-blue-700 font-medium'
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-blue-700/20 hover:bg-blue-50'
                                ]"
                            >
                                {{ reason }}
                            </button>
                        </div>
                    </div>

                    <div class="mb-5">
                        <label class="text-sm font-medium text-gray-700 mb-2 block">Additional comments (optional):</label>
                        <textarea
                            v-model="feedback.comment"
                            class="w-full border-2 border-gray-200 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            rows="3"
                            placeholder="Any other details you'd like to share..."
                        ></textarea>
                    </div>

                    <div class="flex justify-between items-center gap-3">
                        <button
                            @click="cancelFeedback"
                            :disabled="feedback.submitting"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            @click="submitFeedback"
                            :disabled="feedback.submitting"
                            class="px-6 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition-colors bg-blue-600 hover:bg-blue-700"
                        >
                            {{ feedback.submitting ? 'Submitting...' : 'Submit Feedback' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
